import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  combineLatest,
  mergeMap,
  startWith,
  take,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { StarRatingModule } from 'angular-star-rating';
import { AsyncPipe } from '@angular/common';

import { StoreService, TranslateDirective, objectNumberToString } from '@speedy/core';

import { ReviewFormData, ReviewsService } from '../../services/reviews.service';
import { Rating } from '../../interfaces/Review';
import { SpeedyButtonComponent } from '../../../../../shared/components/speedy-button/speedy-button.component';
import { SpeedyTextareaComponent } from '../../../../../shared/components/speedy-textarea/speedy-textarea.component';
import { SpeedyInputComponent } from '../../../../../shared/components/speedy-input/speedy-input.component';

@Component({
  selector: 'app-reviews-form',
  templateUrl: './reviews-form.component.html',
  styleUrls: ['./reviews-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateDirective,
    ReactiveFormsModule,
    SpeedyInputComponent,
    StarRatingModule,
    SpeedyTextareaComponent,
    SpeedyButtonComponent,
    AsyncPipe,
  ],
})
export class AddReviewFormComponent implements OnInit {
  @ViewChild(FormGroupDirective) formGroupDirective!: FormGroupDirective;

  _productId!: number;
  @Input()
  set productId(id: number) {
    this._productId = id;
    this.entity_pk_value.setValue(id);
  }
  get productId() {
    return this._productId;
  }

  @Output() reviewAdded = new EventEmitter<void>();

  loading$ = new BehaviorSubject<boolean>(false);

  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private reviewsService = inject(ReviewsService);
  private alertService = inject(ToastrService);
  private storeService = inject(StoreService);
  private readonly destroyRef = inject(DestroyRef);

  addReviewForm = this.fb.group({
    title: this.fb.control('', Validators.required),
    detail: this.fb.control('', [Validators.required]),
    nickname: this.fb.control('', Validators.required),
    ratings: this.fb.array([
      this.fb.group({
        rating_name: this.fb.control(''),
        value: this.fb.control(0),
      }),
    ]),
    review_entity: this.fb.control('product', { nonNullable: true }),
    review_status: this.fb.control(2, { nonNullable: true }),
    entity_pk_value: this.fb.control(null),
  });

  ngOnInit(): void {
    combineLatest([
      this.storeService.store$.pipe(startWith(this.storeService.getCurrentStore())),
      this.reviewAdded.pipe(startWith(null)),
    ])
      .pipe(
        mergeMap(([store]) =>
          this.reviewsService.getRatings(parseInt(store.store_id)).pipe(take(1)),
        ),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((ratings) => {
        this.setRatingsInfoToForm(ratings);
      });
  }

  setRatingsInfoToForm(ratings: Rating[]) {
    ratings.forEach((rating) => {
      this.ratings.patchValue([
        {
          rating_name: rating.rating_code,
          value: 5,
        },
      ]);

      this.cdr.detectChanges();
    });
  }

  submit() {
    if (!this.addReviewForm.valid) {
      this.addReviewForm.markAllAsTouched();
      return;
    }

    this.loading$.next(true);

    const value = objectNumberToString(
      this.addReviewForm.getRawValue(),
    ) as ReviewFormData;

    this.reviewsService
      .addReview(value)
      .pipe(
        take(1),
        catchError((error) => {
          this.alertService.error(error.error?.message);
          this.loading$.next(false);
          return EMPTY;
        }),
      )
      .subscribe(() => {
        this.cleanFormAfterSubmit();
      });
  }

  cleanFormAfterSubmit() {
    this.loading$.next(false);
    this.alertService.success('The review was added successfully!');
    this.formGroupDirective.reset();
    this.entity_pk_value.setValue(this.productId);
    this.reviewAdded.emit();
  }

  get ratings() {
    return this.addReviewForm.controls.ratings as unknown as FormArray<
      FormGroup<{ rating_name: FormControl<string>; value: FormControl<number> }>
    >;
  }

  get entity_pk_value() {
    return this.addReviewForm.controls.entity_pk_value as FormControl;
  }
}
